import {ListItem} from '@github-ui/list-view'
import {SafeHTMLText} from '@github-ui/safe-html'
import type {Repository} from '@github-ui/current-repository'
import {announce} from '@github-ui/aria-live'
import {commitPath} from '@github-ui/paths'
import {Suspense, lazy, useEffect, useRef, useState} from 'react'
import {CommitAttribution} from '../CommitAttribution'
import {CopySHA} from '../CopySHA'
import type {Commit} from '../../types/shared'
import {CheckStatusDialog, useCommitChecksStatusDetails} from '@github-ui/commit-checks-status'
import {useFindDeferredCommitData} from '../../contexts/DeferredCommitDataContext'
import {
  BrowseRepositoryAtThisPoint,
  ToggleCommitDescription,
  ViewCodeAtThisPoint,
  ViewCommitDetails,
  CommitCommentCount,
} from '../CommitRowActions'
import {verifiedBadgeWidth, SignedCommitBadge, CommitChecksStatusBadge} from '../CommitRowBadges'

export interface CommitRowProps {
  buildCommitPath?: (args: {owner: string; repo: string; commitish: string}) => string
  commit: Commit
  repo: Repository
  path: string
}

// eslint-disable-next-line github/no-then
const CommitActionBar = lazy(() => import('./CommitActionBar').then(m => ({default: m.CommitActionBar})))

export function CommitRow({buildCommitPath, commit, repo, path}: CommitRowProps) {
  const [showDescription, setShowDescription] = useState(false)
  const commitDescriptionRef = useRef<HTMLSpanElement>(null)
  const commitHref = commitPath({owner: repo.ownerLogin, repo: repo.name, commitish: commit.oid})
  const [details, fetchDetails] = useCommitChecksStatusDetails(commit.oid, repo)
  const [isOpen, setIsOpen] = useState(false)
  const deferredData = useFindDeferredCommitData(commit.oid)

  useEffect(() => {
    if (showDescription && commitDescriptionRef.current && commitDescriptionRef.current.textContent) {
      announce(commitDescriptionRef.current.textContent)
    }
  }, [commitDescriptionRef, showDescription])

  return (
    <>
      <ListItem
        data-testid="commit-row-item"
        sx={{
          ':first-child': {
            borderTopLeftRadius: 2,
            borderTopRightRadius: 2,
          },
          ':last-child': {
            borderBottomLeftRadius: 2,
            borderBottomRightRadius: 2,
          },
        }}
        title={
          <ListItem.Title
            value={commit.shortMessage}
            markdownValue={commit.shortMessageMarkdownLink}
            href={commitHref}
            containerSx={{display: 'inline'}}
            headerSx={{display: 'inline'}}
          >
            {commit.bodyMessageHtml && (
              <ToggleCommitDescription
                showDescription={showDescription}
                setShowDescription={setShowDescription}
                oid={commit.oid}
              />
            )}
          </ListItem.Title>
        }
        metadata={
          <>
            <ListItem.Metadata>
              <CommitCommentCount oid={commit.oid} repo={repo} count={deferredData?.commentCount ?? 0} />
            </ListItem.Metadata>

            <ListItem.Metadata sx={{minWidth: verifiedBadgeWidth}}>
              <SignedCommitBadge deferredData={deferredData} />
            </ListItem.Metadata>

            <ListItem.Metadata variant="primary" sx={{px: 0, display: ['none', 'flex'], gap: 2}}>
              <div className="d-flex">
                <ViewCommitDetails buildCommitPath={buildCommitPath} oid={commit.oid} repo={repo} />
                <CopySHA sha={commit.oid} />
              </div>
              <ViewCodeAtThisPoint repo={repo} oid={commit.oid} path={path} />
              <BrowseRepositoryAtThisPoint repo={repo} oid={commit.oid} />
            </ListItem.Metadata>
          </>
        }
        secondaryActions={
          <Suspense>
            <CommitActionBar
              buildCommitPath={buildCommitPath}
              commit={commit}
              repo={repo}
              path={path}
              setDialogOpen={setIsOpen}
              fetchCheckDetails={fetchDetails}
              deferredData={deferredData}
            />
          </Suspense>
        }
      >
        <div className="px-2" />
        <ListItem.MainContent>
          {showDescription && commit.bodyMessageHtml && (
            <ListItem.Description>
              <SafeHTMLText
                ref={commitDescriptionRef}
                html={commit.bodyMessageHtml}
                className="ws-pre-wrap extended-commit-description-container"
                sx={{pb: 2, wordBreak: 'break-word', fontFamily: 'var(--fontStack-monospace)'}}
              />
            </ListItem.Description>
          )}

          <ListItem.Description>
            <CommitAttribution commit={commit} repo={repo}>
              <CommitChecksStatusBadge repository={repo} deferredData={deferredData} oid={commit.oid} />
            </CommitAttribution>
          </ListItem.Description>
        </ListItem.MainContent>
      </ListItem>
      {deferredData?.statusCheckStatus && isOpen && (
        <CheckStatusDialog
          combinedStatus={details}
          isOpen={isOpen}
          onDismiss={() => {
            setIsOpen(false)
          }}
        />
      )}
    </>
  )
}

try{ CommitActionBar.displayName ||= 'CommitActionBar' } catch {}
try{ CommitRow.displayName ||= 'CommitRow' } catch {}